export type QueryValue = string | string[] | undefined;
export type Query = Record<string, QueryValue>;

export type UrlObject = {
  pathname?: string;
  query?: Query;
  hash?: string;
};

export type Url = string | UrlObject;
export type TransitionOptions = {
  shallow?: boolean;
  scroll?: boolean;
  locale?: false;
};

type RouterEventMap = {
  routeChangeStart: [url: string];
  routeChangeComplete: [url: string];
  routeChangeError: [error?: unknown, url?: string];
};
type RouterEventName = keyof RouterEventMap | string;
type RouterEventArgs<TEvent extends RouterEventName> =
  TEvent extends keyof RouterEventMap ? RouterEventMap[TEvent] : unknown[];
type RouterEventHandler<TEvent extends RouterEventName = RouterEventName> = (
  ...args: RouterEventArgs<TEvent>
) => void;

type NavigateFunction = (
  href: string,
  replace: boolean,
  options?: TransitionOptions,
) => Promise<void> | void;

class CompatRouterEvents {
  private listeners = new Map<RouterEventName, Set<RouterEventHandler>>();

  on<TEvent extends RouterEventName>(
    event: TEvent,
    handler: RouterEventHandler<TEvent>,
  ): void {
    const handlers = this.listeners.get(event) ?? new Set<RouterEventHandler>();
    handlers.add(handler as RouterEventHandler);
    this.listeners.set(event, handlers);
  }

  off<TEvent extends RouterEventName>(
    event: TEvent,
    handler: RouterEventHandler<TEvent>,
  ): void {
    const handlers = this.listeners.get(event);
    if (!handlers) return;

    handlers.delete(handler as RouterEventHandler);

    if (handlers.size === 0) {
      this.listeners.delete(event);
    }
  }

  emit<TEvent extends RouterEventName>(
    event: TEvent,
    ...args: RouterEventArgs<TEvent>
  ): void {
    const handlers = this.listeners.get(event);
    if (!handlers) return;

    for (const handler of handlers) {
      (handler as RouterEventHandler<TEvent>)(...args);
    }
  }
}

const events = new CompatRouterEvents();
let navigateFunction: NavigateFunction | null = null;

function parseQuery(search: string): Query {
  const query: Query = {};
  const params = new URLSearchParams(search);

  params.forEach((value, key) => {
    const currentValue = query[key];

    if (currentValue === undefined) {
      query[key] = value;
      return;
    }

    if (Array.isArray(currentValue)) {
      query[key] = [...currentValue, value];
      return;
    }

    query[key] = [currentValue, value];
  });

  return query;
}

function serializeQuery(query?: Query): string {
  if (!query) return "";

  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined) return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        params.append(key, item);
      });
      return;
    }

    params.append(key, value);
  });

  return params.toString();
}

function getCurrentPathname(): string {
  if (typeof window === "undefined") return "/";
  return window.location.pathname || "/";
}

function getCurrentSearch(): string {
  if (typeof window === "undefined") return "";
  return window.location.search;
}

function getCurrentHash(): string {
  if (typeof window === "undefined") return "";
  return window.location.hash;
}

function isExternalUrl(url: string): boolean {
  return /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url);
}

function getAbsoluteUrl(href: string): URL {
  if (typeof window === "undefined") {
    return new URL(href, "http://localhost");
  }

  return new URL(href, window.location.origin);
}

export function createHref(url: Url): string {
  if (typeof url === "string") {
    if (!url) {
      return `${getCurrentPathname()}${getCurrentSearch()}${getCurrentHash()}`;
    }

    if (url.startsWith("#")) {
      return `${getCurrentPathname()}${getCurrentSearch()}${url}`;
    }

    if (url.startsWith("?")) {
      return `${getCurrentPathname()}${url}`;
    }

    if (isExternalUrl(url)) {
      return url;
    }

    const absoluteUrl = getAbsoluteUrl(url);
    return `${absoluteUrl.pathname}${absoluteUrl.search}${absoluteUrl.hash}`;
  }

  const pathname = url.pathname || getCurrentPathname();
  const search = serializeQuery(url.query);
  const hash = url.hash
    ? url.hash.startsWith("#")
      ? url.hash
      : `#${url.hash}`
    : "";

  return `${pathname}${search ? `?${search}` : ""}${hash}`;
}

function createSnapshot() {
  const pathname = getCurrentPathname();
  const search = getCurrentSearch();
  const hash = getCurrentHash();

  return {
    pathname,
    route: pathname,
    asPath: `${pathname}${search}${hash}`,
    query: parseQuery(search),
    isReady: true,
  };
}

export function setCompatRouterNavigate(fn: NavigateFunction): void {
  navigateFunction = fn;
}

async function navigate(
  target: Url,
  replace = false,
  as?: Url,
  options?: TransitionOptions,
): Promise<boolean> {
  const href = createHref(as ?? target);
  events.emit("routeChangeStart", href);

  try {
    if (navigateFunction) {
      await navigateFunction(href, replace, options);
    } else if (typeof window !== "undefined") {
      if (replace) {
        window.location.replace(href);
      } else {
        window.location.assign(href);
      }
    }

    events.emit("routeChangeComplete", href);
    return true;
  } catch (error) {
    events.emit("routeChangeError", error, href);
    throw error;
  }
}

class CompatRouter {
  get pathname(): string {
    return createSnapshot().pathname;
  }

  get route(): string {
    return createSnapshot().route;
  }

  get asPath(): string {
    return createSnapshot().asPath;
  }

  get query(): Query {
    return createSnapshot().query;
  }

  get isReady(): boolean {
    return true;
  }

  readonly events = events;

  readonly push = async (
    url: Url,
    as?: Url,
    options?: TransitionOptions,
  ): Promise<boolean> => {
    return navigate(url, false, as, options);
  };

  readonly replace = async (
    url: Url,
    as?: Url,
    options?: TransitionOptions,
  ): Promise<boolean> => {
    return navigate(url, true, as, options);
  };

  readonly back = (): void => {
    if (typeof window === "undefined") return;

    events.emit("routeChangeStart", "back");
    window.history.back();
  };

  readonly reload = (): void => {
    if (typeof window === "undefined") return;
    window.location.reload();
  };

  readonly prefetch = async (): Promise<void> => {};
}

export const compatRouter = new CompatRouter();

if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    events.emit(
      "routeChangeComplete",
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    );
  });
}

export function getCompatRouterSnapshot() {
  return createSnapshot();
}
